import { AiFillEye } from "react-icons/ai";
import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllClients } from "../../../redux/actions/authActions";
import SearchBar from "../../../components/searchbar/SearchBar";
import { Tag, Select, Tooltip, Popover, Modal, Input } from "antd";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";

const { Option } = Select;

const TaskManagementDatatable = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [modalClientData, setModalClientData] = useState(null);
  const [breakdownTab, setBreakdownTab] = useState("all");
  const [breakdownSearch, setBreakdownSearch] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const clients = useSelector((state) => state.auth.allClients || []);

  useEffect(() => {
    async function func() {
      try {
        if (token) {
          await dispatch(getAllClients(token));
        }
      } catch (err) {
        console.log(err);
      }
    }
    func();
  }, [dispatch, token]);

  // Extract available years from clients' taskStats
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    const currentYear = new Date().getFullYear();
    // Include past years back to 2010
    for (let yr = 2010; yr <= currentYear; yr++) {
      yearSet.add(yr);
    }
    // Also include any other years from client records
    (clients || []).forEach((c) => {
      if (c.taskStats?.years && Array.isArray(c.taskStats.years)) {
        c.taskStats.years.forEach((yr) => yearSet.add(yr));
      }
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [clients]);

  const handleSearch = (e) => {
    setSearchQuery(e || "");
  };

  const filteredRows = useMemo(() => {
    return (clients || []).filter((r) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (r.businessName || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.id || "").toLowerCase().includes(q);

      if (!matchesSearch) return false;

      if (selectedYear !== "all") {
        const clientYears = r.taskStats?.years || [];
        if (!clientYears.includes(Number(selectedYear))) {
          return false;
        }
      }

      return true;
    });
  }, [clients, searchQuery, selectedYear]);

  const handleViewClick = (id) => {
    navigate(`view-client?clientId=${id}`);
  };

  const columns = [
    {
      field: "id",
      headerName: "Client ID",
      width: 105,
      renderCell: (params) => (
        <span className="font-mono text-xs font-semibold text-gray-700">
          {params.value || "-"}
        </span>
      ),
    },
    {
      field: "businessName",
      headerName: "Company Name",
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value || "Company"}>
          <span className="font-semibold text-gray-900 truncate">
            {params.value || params.row.name || "N/A"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Client SPOC Name",
      width: 140,
      renderCell: (params) => (
        <div className="flex items-center gap-1.5">
          {/* <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
            {(params.value || "C")[0].toUpperCase()}
          </div> */}
          <span className="text-gray-700 font-medium truncate">
            {params.value || "-"}
          </span>
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Client Email",
      width: 160,
      renderCell: (params) => (
        <span className="text-gray-500 text-xs truncate">{params.value || "-"}</span>
      ),
    },
    {
      field: "dueDate",
      headerName: "Upcoming Due Date",
      width: 145,
      renderCell: (params) => {
        const taskDue = params.row.taskStats?.dueDate || params.row.dueDate;
        if (!taskDue) {
          return (
            <span className="text-xs text-gray-400 italic font-normal">
              No tasks
            </span>
          );
        }
        const d = new Date(taskDue);
        if (isNaN(d.getTime())) {
          return <span className="text-gray-400 text-xs">-</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <CalendarOutlined className="text-amber-500 text-xs shrink-0" />
            <span className="text-xs font-bold text-gray-800">
              {d.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
        );
      },
    },
    {
      field: "taskStats",
      headerName: "Task Status",
      width: 250,
      renderCell: (params) => {
        const stats = params.value || {
          total: 0,
          completed: 0,
          inProgress: 0,
          pending: 0,
          upcoming: 0,
          tasks: [],
        };
        const percent =
          stats.total > 0
            ? Math.round((stats.completed / stats.total) * 100)
            : 0;

        if (stats.total === 0) {
          return (
            <span className="text-gray-400 text-xs italic">
              No tasks assigned
            </span>
          );
        }

        const clientName = params.row.businessName || params.row.name || "Client";
        const tasksList = stats.tasks || [];
        const completedList = tasksList.filter(
          (t) => (t.status || "").toLowerCase().replace(/\s+/g, "") === "completed"
        );
        const inProgressList = tasksList.filter(
          (t) => (t.status || "").toLowerCase().replace(/\s+/g, "") === "inprogress"
        );
        const pendingList = tasksList.filter(
          (t) =>
            (t.status || "").toLowerCase().replace(/\s+/g, "") === "pending" ||
            !t.status
        );

        const popoverContent = (
          <div style={{ width: 330, maxHeight: 380 }} className="p-1 overflow-y-auto">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
              <div>
                <span className="font-bold text-xs text-gray-800 block truncate max-w-[210px]">
                  {clientName}
                </span>
                <span className="text-[10px] text-gray-500">
                  Total {stats.total} Tasks
                </span>
              </div>
              <Tag color="blue" className="!m-0 text-[11px] font-bold">
                {percent}% Completed
              </Tag>
            </div>

            {/* In Progress */}
            {inProgressList.length > 0 && (
              <div className="mb-2 bg-blue-50/80 p-2 rounded-lg border border-blue-100">
                <div className="text-[11px] font-bold text-blue-700 flex items-center justify-between mb-1">
                  <span>In Progress ({inProgressList.length})</span>
                </div>
                <div className="space-y-1">
                  {inProgressList.map((t, idx) => (
                    <div
                      key={idx}
                      className="bg-white px-2 py-1 rounded text-xs text-gray-700 flex items-center justify-between border border-blue-50 shadow-2xs"
                    >
                      <span className="truncate max-w-[190px] font-medium">{t.name}</span>
                      <span className="text-[10px] text-blue-600 font-semibold">{t.progress || 50}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending */}
            {pendingList.length > 0 && (
              <div className="mb-2 bg-amber-50/80 p-2 rounded-lg border border-amber-100">
                <div className="text-[11px] font-bold text-amber-700 flex items-center justify-between mb-1">
                  <span>Pending Tasks ({pendingList.length})</span>
                </div>
                <div className="space-y-1">
                  {pendingList.map((t, idx) => (
                    <div
                      key={idx}
                      className="bg-white px-2 py-1 rounded text-xs text-gray-700 flex items-center justify-between border border-amber-50 shadow-2xs"
                    >
                      <span className="truncate max-w-[190px] font-medium">{t.name}</span>
                      <span className="text-[10px] text-amber-600 font-semibold">
                        {t.dueDate
                          ? new Date(t.dueDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                            })
                          : "No date"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {completedList.length > 0 && (
              <div className="mb-1 bg-emerald-50/80 p-2 rounded-lg border border-emerald-100">
                <div className="text-[11px] font-bold text-emerald-700 flex items-center justify-between mb-1">
                  <span>✓ Completed ({completedList.length})</span>
                </div>
                <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
                  {completedList.map((t, idx) => (
                    <div
                      key={idx}
                      className="bg-white px-2 py-1 rounded text-xs text-gray-700 flex items-center justify-between border border-emerald-50 shadow-2xs"
                    >
                      <span className="truncate max-w-[210px] text-gray-700 font-medium">
                        {t.name}
                      </span>
                      <span className="text-[10px] text-emerald-600 font-bold">Completed</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tasksList.length === 0 && (
              <p className="text-center text-gray-400 text-xs py-2 italic">
                Click View to inspect tasks
              </p>
            )}

            {tasksList.length > 0 && (
              <button
                type="button"
                onClick={() => setModalClientData(params.row)}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline block text-center w-full pt-2 border-t border-gray-100 mt-1"
              >
                Open Full Tasks Modal
              </button>
            )}
          </div>
        );

        return (
          <Popover
            content={popoverContent}
            title={null}
            trigger={["hover", "click"]}
            placement="bottom"
          >
            <div
              style={{ lineHeight: "1.2", cursor: "pointer" }}
              onClick={() => setModalClientData(params.row)}
              className="flex flex-col justify-center w-full py-1 group"
            >
              <div className="flex items-center gap-1.5 flex-nowrap mb-1.5">
                <Tag color="default" className="!m-0 text-[11px] font-semibold shrink-0">
                  Total: {stats.total}
                </Tag>
                {stats.completed > 0 && (
                  <Tag color="success" className="!m-0 text-[11px] font-semibold shrink-0">
                    ✓ {stats.completed} Completed
                  </Tag>
                )}
                {stats.inProgress > 0 && (
                  <Tag color="processing" className="!m-0 text-[11px] font-semibold shrink-0">
                    {stats.inProgress} In Prog
                  </Tag>
                )}
                {stats.pending > 0 && (
                  <Tag color="warning" className="!m-0 text-[11px] font-semibold shrink-0">
                    {stats.pending} Pending
                  </Tag>
                )}
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-2 w-full max-w-[200px]">
                <div className="flex-1 bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full rounded-full transition-all duration-300 group-hover:brightness-95"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <span className="text-[11px] font-bold text-gray-600 shrink-0">
                  {percent}%
                </span>
              </div>
            </div>
          </Popover>
        );
      },
    },
    { field: "clientType", headerName: "Client Type", width: 120 },
   
    {
      field: "view",
      headerName: "View",
      width: 80,
      renderCell: (params) => {
        return (
          <button
            className="bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg text-blue-700 border border-blue-200 shadow-sm transition-colors flex items-center gap-1 text-xs font-semibold"
            onClick={() => handleViewClick(params.row._id)}
          >
            <AiFillEye size={15} />
            <span>View</span>
          </button>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 min-w-[280px]">
          <SearchBar
            titleOne="Add Client"
            onSearch={handleSearch}
            addClientPath="/client-management/add-client"
            showAddButton={true}
          />
        </div>

        {/* Year Filter Dropdown */}
        <div className="flex items-center gap-2 pb-4 pr-4">
          <CalendarOutlined className="text-gray-500 text-base" />
          <span className="text-xs font-semibold text-gray-600">Filter Year:</span>
          <Select
            showSearch
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            style={{ width: 130 }}
            size="middle"
            filterOption={(input, option) =>
              String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            <Option value="all">All Years</Option>
            {availableYears.map((yr) => (
              <Option key={yr} value={String(yr)}>
                {yr}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Table container */}
      <div className="w-full">
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row._id || row.id}
          pageSizeOptions={[5, 8, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 8, page: 0 } },
          }}
          pagination
          autoHeight
          rowHeight={74}
          sx={{
            "& .MuiDataGrid-row": {
              minHeight: "74px !important",
              maxHeight: "74px !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              fontWeight: "bold",
              fontSize: "0.875rem",
              borderBottom: "1px solid #e2e8f0",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              lineHeight: "normal !important",
              minHeight: "74px !important",
              maxHeight: "74px !important",
              borderBottom: "1px solid #f1f5f9",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8fafc",
            },
            border: "none",
          }}
        />
      </div>

      {/* ── TASKS BREAKDOWN MODAL (ON HOVER POPUP / ON CLICK) ── */}
      <Modal
        open={Boolean(modalClientData)}
        onCancel={() => {
          setModalClientData(null);
          setBreakdownSearch("");
          setBreakdownTab("all");
        }}
        footer={null}
        width={720}
        title={
          <div className="flex items-center justify-between border-b border-gray-100 pb-3 pr-6">
            <div>
              <span className="text-base font-bold text-gray-800 block">
                {modalClientData?.businessName || modalClientData?.name || "Client"} - Tasks Breakdown
              </span>
              <span className="text-xs text-gray-500 font-normal">
                SPOC: {modalClientData?.name || "-"} | Total {modalClientData?.taskStats?.total || 0} Tasks
              </span>
            </div>
            <Tag color="blue" className="!m-0 text-xs font-bold">
              {modalClientData?.taskStats?.total > 0
                ? Math.round(((modalClientData.taskStats.completed || 0) / modalClientData.taskStats.total) * 100)
                : 0}
              % Completed
            </Tag>
          </div>
        }
      >
        {modalClientData && (() => {
          const stats = modalClientData.taskStats || { total: 0, completed: 0, inProgress: 0, pending: 0, tasks: [] };
          const tasks = stats.tasks || [];
          const inProgList = tasks.filter((t) => (t.status || "").toLowerCase().replace(/\s+/g, "") === "inprogress");
          const pendList = tasks.filter((t) => (t.status || "").toLowerCase().replace(/\s+/g, "") === "pending" || !t.status);
          const doneList = tasks.filter((t) => (t.status || "").toLowerCase().replace(/\s+/g, "") === "completed");

          const filteredList = tasks.filter((t) => {
            const s = (t.status || "").toLowerCase().replace(/\s+/g, "");
            if (breakdownTab === "inprogress" && s !== "inprogress") return false;
            if (breakdownTab === "pending" && s !== "pending" && s !== "") return false;
            if (breakdownTab === "completed" && s !== "completed") return false;

            if (breakdownSearch.trim()) {
              const q = breakdownSearch.toLowerCase().trim();
              const tName = t.name || "";
              const tId = t.id || "";
              return tName.toLowerCase().includes(q) || tId.toLowerCase().includes(q);
            }
            return true;
          });

          return (
            <div className="pt-2">
              {/* Filter Tabs */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setBreakdownTab("all")}
                  className={`p-2.5 rounded-lg border text-center transition-all cursor-pointer ${
                    breakdownTab === "all"
                      ? "bg-blue-50 border-blue-500 text-blue-700 font-bold shadow-xs"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-base font-extrabold">{tasks.length}</div>
                  <div className="text-[11px] font-medium">All Tasks</div>
                </button>
                <button
                  type="button"
                  onClick={() => setBreakdownTab("inprogress")}
                  className={`p-2.5 rounded-lg border text-center transition-all cursor-pointer ${
                    breakdownTab === "inprogress"
                      ? "bg-blue-50 border-blue-500 text-blue-700 font-bold shadow-xs"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-base font-extrabold text-blue-600">{inProgList.length}</div>
                  <div className="text-[11px] font-medium">⏳ In Progress</div>
                </button>
                <button
                  type="button"
                  onClick={() => setBreakdownTab("pending")}
                  className={`p-2.5 rounded-lg border text-center transition-all cursor-pointer ${
                    breakdownTab === "pending"
                      ? "bg-amber-50 border-amber-500 text-amber-700 font-bold shadow-xs"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-base font-extrabold text-amber-600">{pendList.length}</div>
                  <div className="text-[11px] font-medium">⚠️ Pending</div>
                </button>
                <button
                  type="button"
                  onClick={() => setBreakdownTab("completed")}
                  className={`p-2.5 rounded-lg border text-center transition-all cursor-pointer ${
                    breakdownTab === "completed"
                      ? "bg-emerald-50 border-emerald-500 text-emerald-700 font-bold shadow-xs"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-base font-extrabold text-emerald-600">{doneList.length}</div>
                  <div className="text-[11px] font-medium">✓ Completed</div>
                </button>
              </div>

              {/* Quick Search */}
              <div className="mb-3">
                <Input
                  placeholder="Search task by name or ID..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={breakdownSearch}
                  onChange={(e) => setBreakdownSearch(e.target.value)}
                  allowClear
                  className="rounded-lg"
                />
              </div>

              {/* Tasks Table List */}
              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-100 text-gray-600 sticky top-0 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="py-2.5 px-3 w-10 font-bold text-center">#</th>
                      <th className="py-2.5 px-3 font-bold">Task Name & ID</th>
                      <th className="py-2.5 px-3 font-bold">Status</th>
                      <th className="py-2.5 px-3 font-bold">Progress</th>
                      <th className="py-2.5 px-3 font-bold">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredList.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-400 italic">
                          No tasks found for the selected filter.
                        </td>
                      </tr>
                    ) : (
                      filteredList.map((t, idx) => {
                        const s = (t.status || "").toLowerCase().replace(/\s+/g, "");
                        const isDone = s === "completed";
                        const isProg = s === "inprogress";
                        const prog = t.progress || (isDone ? 100 : isProg ? 50 : 0);

                        let tagColor = "warning";
                        let tagLabel = "! Pending";
                        if (isDone) {
                          tagColor = "success";
                          tagLabel = "✓ Completed";
                        } else if (isProg) {
                          tagColor = "processing";
                          tagLabel = "⏳ In Progress";
                        }

                        return (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="py-2.5 px-3 text-center text-gray-400 font-medium">
                              {idx + 1}
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="font-semibold text-gray-800">{t.name}</div>
                              {t.id && (
                                <div className="text-[10px] font-mono text-gray-500">{t.id}</div>
                              )}
                            </td>
                            <td className="py-2.5 px-3">
                              <Tag color={tagColor} className="!m-0 text-xs font-semibold">
                                {tagLabel}
                              </Tag>
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-1.5 w-24">
                                <div className="flex-1 bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                  <div
                                    className="bg-emerald-500 h-full rounded-full"
                                    style={{ width: `${prog}%` }}
                                  />
                                </div>
                                <span className="text-[10px] font-bold text-gray-600">{prog}%</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 font-medium text-gray-700">
                              {t.dueDate ? (
                                new Date(t.dueDate).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              ) : (
                                <span className="text-gray-400 italic">No date</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </Modal>
    </>
  );
};

export default TaskManagementDatatable;
